import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { UserContext } from '../userContext';
import PdfViewer from '../components/PdfViewer';
import { downloadPdfData } from '../utils/pdfAccess';
import { getEffectivePlan } from '../utils/effectivePlan';
import './VerConteudo.css';
import {
  FaCalendarAlt,
  FaUser,
  FaTags,
  FaHeart,
  FaBookmark,
  FaPlay,
  FaExpand,
  FaDownload,
  FaFilePdf,
  FaImage,
  FaTimes,
  FaUserGraduate,
  FaReply,
  FaShareAlt,
  FaCheckCircle
} from 'react-icons/fa';

const PDF_VIEW_MODES = {
  INTERNAL: 'sdk',
  BROWSER: 'native'
};

function VerConteudo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [conteudo, setConteudo] = useState(null);
  const [comentario, setComentario] = useState('');
  const [comentarios, setComentarios] = useState([]);
  const [curtidas, setCurtidas] = useState(0);
  const [favoritado, setFavoritado] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isStudentMaterial, setIsStudentMaterial] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  const [pdfViewMode, setPdfViewMode] = useState(() => {
    if (typeof window === 'undefined') return PDF_VIEW_MODES.INTERNAL;
    const savedMode = window.localStorage.getItem('pdf_view_mode');
    return savedMode === PDF_VIEW_MODES.BROWSER ? PDF_VIEW_MODES.BROWSER : PDF_VIEW_MODES.INTERNAL;
  });

  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    actionType: 'content' // 'content', 'login', 'login_or_subscribe'
  });

  const effectivePlan = getEffectivePlan(user);
  const canPlay = effectivePlan.hasPlanAccess;
  const isLoggedIn = !!user?.uid;

  // Função para verificar se é especialista/admin
  const isEspecialista = (material) => {
    // Materiais da collection 'materials' são sempre de especialistas/admin
    if (material.source === 'admin' || !isStudentMaterial) {
      return true;
    }

    // Para materiais de alunos, verificar se o autor tem perfil de especialista
    // Pode verificar por email, nome ou campo específico
    const especialistaEmails = [
      'admin@conecta.com',
      'professor@conecta.com',
      'especialista@conecta.com'
    ];

    const especialistaNomes = [
      'Conecta Admin',
      'Conecta Admin Master',
      'Professor',
      'Especialista'
    ];

    return especialistaEmails.includes(material.authorEmail) ||
      especialistaNomes.includes(material.authorName) ||
      material.isEspecialista === true;
  };

  // Função para determinar se um link é YouTube
  const isYouTubeLink = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Função para determinar se um link é Vimeo
  const isVimeoLink = (url) => {
    if (!url) return false;
    return url.includes('vimeo.com');
  };

  // Função para obter a URL do material considerando linkRelacionado
  const getMaterialUrl = (material) => {
    const directUrl = (material?.materialUrl || material?.arquivoURL || material?.videoUrl || '').trim();

    // Se é material de aluno e tem linkRelacionado com YouTube/Vimeo, usar ele
    if (isStudentMaterial && material.linkRelacionado) {
      if (isYouTubeLink(material.linkRelacionado) || isVimeoLink(material.linkRelacionado)) {
        return material.linkRelacionado;
      }
    }

    // Caso contrário, usar materialUrl normal
    return directUrl;
  };

  // Função para obter o tipo de material considerando linkRelacionado
  const getMaterialType = (material) => {
    const materialUrl = getMaterialUrl(material);

    // Se tem linkRelacionado de YouTube/Vimeo, definir tipo baseado na URL
    if (isStudentMaterial && material.linkRelacionado && materialUrl === material.linkRelacionado) {
      if (isYouTubeLink(materialUrl)) return 'YOUTUBE';
      if (isVimeoLink(materialUrl)) return 'VIMEO';
    }

    // Caso contrário, usar materialType original
    return material.materialType?.toUpperCase();
  };

  // ---- helpers ----
  const formatCreatedAt = (createdAt) => {
    try {
      if (!createdAt) return '';
      // Firestore Timestamp (objeto com seconds)
      if (typeof createdAt?.seconds === 'number') {
        return new Date(createdAt.seconds * 1000).toLocaleDateString('pt-BR');
      }
      // Firestore Timestamp (método toDate)
      if (typeof createdAt?.toDate === 'function') {
        return createdAt.toDate().toLocaleDateString('pt-BR');
      }
      // ISO string
      const d = new Date(createdAt);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
      return '';
    } catch {
      return '';
    }
  };

  // Função para formatar texto com bold, quebras de linha e parágrafos
  const formatText = (text) => {
    if (!text) return '';

    return text
      // Converte **texto** em <strong>texto</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Converte quebras de linha duplas em parágrafos
      .split('\n\n')
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => {
        // Converte quebras de linha simples em <br>
        const formattedParagraph = paragraph.replace(/\n/g, '<br>');
        return `<p>${formattedParagraph}</p>`;
      })
      .join('');
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/conteudo/${id}`;
    const title = conteudo?.title || 'Conteúdo';
    const text =
      'Acesse este conteúdo na EAD Conect. A pré-visualização é aberta; o conteúdo completo requer login.';

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareMsg('Link copiado!');
        setTimeout(() => setShareMsg(''), 2000);
      } else {
        // fallback mais simples
        window.prompt('Copie o link:', shareUrl);
      }
    } catch {
      // usuário cancelou ou não copiou – só ignora
    }
  };

  const getDownloadFileName = (title) => {
    const baseName = String(title || 'documento')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

    return `${baseName || 'documento'}.pdf`;
  };

  const handleDownloadPdf = async () => {
    const pdfUrl = (getMaterialUrl(conteudo) || '').trim();
    if (!pdfUrl || isDownloading) return;

    setIsDownloading(true);

    try {
      const data = await downloadPdfData(pdfUrl);
      const blob = new Blob([data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getDownloadFileName(conteudo?.title);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      window.alert(error?.message || 'Nao foi possivel baixar o PDF.');
    } finally {
      setIsDownloading(false);
    }
  };

  // -----------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('pdf_view_mode', pdfViewMode);
  }, [pdfViewMode]);

  useEffect(() => {
    let cancelled = false;

    async function fetchConteudo() {
      setConteudo(null);
      setComentarios([]);
      setFavoritado(false);
      setIsStudentMaterial(false);

      // Primeiro tenta buscar nos materiais dos professores/admin
      let ref = doc(db, 'materials', id);
      let snap = await getDoc(ref);

      if (cancelled) return;

      if (snap.exists()) {
        const data = snap.data();
        if (cancelled) return;
        setConteudo({ id: snap.id, ...data });
        setCurtidas(data.likes || 0);
        setIsStudentMaterial(false);
        return;
      }

      // Tenta buscar nos materiais dos alunos
      ref = doc(db, 'student_materials', id);
      snap = await getDoc(ref);

      if (cancelled || !snap.exists()) return;

      // Mapear para os campos usados na página
      const data = snap.data();
      const mappedData = {
        id: snap.id,
        title: data.titulo,
        description: data.descricao,
        materialType: data.materialType,
        materialUrl: data.arquivoURL || data.materialUrl || data.videoUrl,
        linkRelacionado: data.linkRelacionado, // ADICIONADO: campo linkRelacionado
        coverImage: data.coverImage || data.arquivoURL || data.materialUrl || data.videoUrl,
        authorName: data.authorName,
        authorEmail: data.authorEmail,
        createdAt: data.createdAt,
        subcategory: data.subcategoria,
        category: data.categoria,
        // campos específicos
        conteudo: data.conteudo,
        resposta: data.resposta,
        videoResposta: data.videoResposta,
        respondidoEm: data.respondidoEm,
        respondidoPor: data.respondidoPor,
        status: data.status,
        tipo: data.tipo,
        hasThumbnail: !!data.coverImage,
        isEspecialista: data.isEspecialista
      };

      if (cancelled) return;
      setConteudo(mappedData);
      setCurtidas(data.likes || 0);
      setIsStudentMaterial(true);
    }

    fetchConteudo();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    if (!conteudo?.id) return () => {
      cancelled = true;
    };

    async function fetchFavoritoEComentarios() {
      const collection_name = isStudentMaterial ? 'student_materials' : 'materials';

      const snapComentarios = await getDocs(collection(db, collection_name, id, 'comments'));
      if (!cancelled) {
        const dados = snapComentarios.docs.map((d) => ({ id: d.id, ...d.data() }));
        const ordenados = dados.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setComentarios(ordenados);
      }

      if (!user?.uid) {
        if (!cancelled) setFavoritado(false);
        return;
      }

      const favSnap = await getDoc(doc(db, collection_name, id, 'favorites', user.uid));
      if (!cancelled) setFavoritado(favSnap.exists());
    }

    fetchFavoritoEComentarios();

    return () => {
      cancelled = true;
    };
  }, [id, conteudo?.id, isStudentMaterial, user?.uid]);

  const enviarComentario = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setModalConfig({
        title: 'Login Necessário',
        message: 'Você precisa fazer login para comentar.',
        actionType: 'login'
      });
      setShowModal(true);
      return;
    }
    if (!comentario.trim()) return;

    const collection_name = isStudentMaterial ? 'student_materials' : 'materials';
    await addDoc(collection(db, collection_name, id, 'comments'), {
      texto: comentario,
      autor: user.name || user.email,
      likes: 0,
      createdAt: serverTimestamp()
    });
    setComentario('');
    const snap = await getDocs(collection(db, collection_name, id, 'comments'));
    const dados = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setComentarios(dados.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
  };

  const curtirComentario = async (cid) => {
    if (!isLoggedIn) {
      setModalConfig({
        title: 'Login Necessário',
        message: 'Você precisa fazer login para curtir comentários.',
        actionType: 'login'
      });
      setShowModal(true);
      return;
    }
    const collection_name = isStudentMaterial ? 'student_materials' : 'materials';
    const ref = doc(db, collection_name, id, 'comments', cid);
    const snap = await getDoc(ref);
    const atual = snap.data();
    await updateDoc(ref, { likes: (atual.likes || 0) + 1 });
    setComentarios((prev) => prev.map((c) => (c.id === cid ? { ...c, likes: (c.likes || 0) + 1 } : c)));
  };

  const curtirVideo = async () => {
    if (!isLoggedIn) {
      setModalConfig({
        title: 'Login Necessário',
        message: 'Você precisa fazer login para curtir este conteúdo.',
        actionType: 'login'
      });
      setShowModal(true);
      return;
    }
    const collection_name = isStudentMaterial ? 'student_materials' : 'materials';
    const ref = doc(db, collection_name, id);
    await updateDoc(ref, { likes: curtidas + 1 });
    setCurtidas(curtidas + 1);
  };

  const favoritarVideo = async () => {
    if (!isLoggedIn) {
      setModalConfig({
        title: 'Login Necessário',
        message: 'Você precisa fazer login para favoritar este conteúdo.',
        actionType: 'login'
      });
      setShowModal(true);
      return;
    }
    const collection_name = isStudentMaterial ? 'student_materials' : 'materials';
    const ref = doc(db, collection_name, id, 'favorites', user.uid);
    if (favoritado) {
      await deleteDoc(ref);
      setFavoritado(false);
      return;
    }
    await setDoc(ref, { favoritado: true, data: serverTimestamp() });
    setFavoritado(true);
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handlePdfModeChange = (nextMode) => {
    if (isFullscreen) return;
    if (nextMode === pdfViewMode) return;
    setIsFullscreen(false);
    setPdfViewMode(nextMode);
  };

  // Thumbnail de vídeo (igual à listagem para consistência)
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return null;

    // YouTube - usar maxresdefault para alta qualidade
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }

    // Vimeo - usar thumbnail de alta resolução
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://i.vimeocdn.com/video/${videoId}_640x360.jpg` : null;
    }

    return null;
  };

  // Prévia para bloqueados (otimizada como na listagem)
  const getLockedPreview = (material) => {
    const materialType = getMaterialType(material); // USAR função que considera linkRelacionado
    const materialUrl = getMaterialUrl(material); // USAR função que considera linkRelacionado

    // Para vídeos, usar mesma lógica da listagem
    if (materialType === 'YOUTUBE' || materialType === 'VIMEO') {
      // PRIORIDADE 1: Se tem coverImage gerada no cadastro, usa ela
      if (material.coverImage && material.coverImage !== materialUrl && material.hasThumbnail) {
        return material.coverImage;
      }

      // PRIORIDADE 2: Se tem coverImage personalizada diferente da URL principal
      if (material.coverImage && material.coverImage !== materialUrl) {
        return material.coverImage;
      }

      // PRIORIDADE 3: Thumbnail automática do vídeo
      return getVideoThumbnail(materialUrl);
    }

    // Para outros tipos, manter lógica original
    return (
      material.coverImage ||
      material.thumbnail ||
      (materialType !== 'PDF' ? materialUrl : null) ||
      null
    );
  };

  // Render do player/visualização
  const renderContent = () => {
    if (!conteudo) return null;

    const materialType = getMaterialType(conteudo); // USAR função que considera linkRelacionado
    const materialUrl = getMaterialUrl(conteudo); // USAR função que considera linkRelacionado

    // Vídeo
    if (materialType === 'YOUTUBE' || materialType === 'VIMEO') {
      const iframeUrl = (() => {
        // YouTube
        if (materialType === 'YOUTUBE') {
          const idMatch = materialUrl?.match(/(?:v=|youtu\.be\/)([^&\n?#]+)/)?.[1] || '';
          const params = new URLSearchParams({
            modestbranding: '1',
            rel: '0',
            iv_load_policy: '3',
            playsinline: '1'
          });
          if (typeof window !== 'undefined' && window.location?.origin) {
            params.set('origin', window.location.origin);
          }
          return `https://www.youtube-nocookie.com/embed/${idMatch}?${params.toString()}`;
        }
        // Vimeo
        if (materialType === 'VIMEO') {
          const idMatch = materialUrl?.match(/vimeo\.com\/([0-9]+)/)?.[1] || '';
          return `https://player.vimeo.com/video/${idMatch}`;
        }
        return '';
      })();

      const lockedPreview = getLockedPreview(conteudo);

      return (
        <div className="video-wrapper">
          {canPlay ? (
            <>
              <iframe
                src={iframeUrl}
                title={conteudo.title}
                frameBorder="0"
                sandbox={materialType === 'YOUTUBE' ? 'allow-scripts allow-same-origin allow-presentation allow-fullscreen' : undefined}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              {materialType === 'YOUTUBE' && (
                <>
                  <div className="yt-no-click-top" aria-hidden="true" />
                  <div className="yt-no-click-br" aria-hidden="true" />
                </>
              )}
            </>
          ) : (
            <div className="content-locked">
              {lockedPreview && (
                <img
                  src={lockedPreview}
                  alt="Preview do vídeo"
                  className="thumb-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              <button
                className="play-overlay"
                onClick={() => {
                  if (!isLoggedIn) {
                    setModalConfig({
                      title: 'Efetue Login ou Assinatura Necessária',
                      message:
                        'Você precisa de uma assinatura para assistir este vídeo. Se já é assinante, faça login!',
                      actionType: 'login_or_subscribe'
                    });
                  } else {
                    setModalConfig({
                      title: 'Assinatura Necessária',
                      message: 'Você precisa de uma assinatura para assistir este vídeo.',
                      actionType: 'content'
                    });
                  }
                  setShowModal(true);
                }}
              >
                <FaPlay size={32} />
              </button>
            </div>
          )}
        </div>
      );
    }

    // PDF
    if (materialType === 'PDF') {
      const lockedPreview = getLockedPreview(conteudo);
      const normalizedPdfUrl = (materialUrl || '').trim();
      const nativePdfViewerUrl = normalizedPdfUrl
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(normalizedPdfUrl)}&embedded=true`
        : '';

      return (
        <div className="pdf-wrapper">
          {canPlay ? (
            <div className={`pdf-container ${isFullscreen ? 'fullscreen' : ''}`}>
              {!isFullscreen && (
                <div className="pdf-view-mode-switch">
                  <div className="pdf-view-mode-actions" role="tablist" aria-label="Tipo de visualização do PDF">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={pdfViewMode === PDF_VIEW_MODES.INTERNAL}
                      className={`pdf-view-mode-btn ${pdfViewMode === PDF_VIEW_MODES.INTERNAL ? 'active' : ''}`}
                      onClick={() => handlePdfModeChange(PDF_VIEW_MODES.INTERNAL)}
                    >
                      Página por página
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={pdfViewMode === PDF_VIEW_MODES.BROWSER}
                      className={`pdf-view-mode-btn ${pdfViewMode === PDF_VIEW_MODES.BROWSER ? 'active' : ''}`}
                      onClick={() => handlePdfModeChange(PDF_VIEW_MODES.BROWSER)}
                    >
                      Abrir no navegador
                    </button>
                  </div>
                  <span className="pdf-view-mode-hint">Escolha como quer visualizar o documento</span>
                </div>
              )}

              {!normalizedPdfUrl ? (
                <div className="unsupported-content" style={{ margin: '1rem' }}>
                  <p>URL do PDF não encontrada para este conteúdo.</p>
                </div>
              ) : pdfViewMode === PDF_VIEW_MODES.INTERNAL ? (
                <PdfViewer
                  storageUrl={normalizedPdfUrl}
                  title={conteudo.title}
                  isFullscreen={isFullscreen}
                  onToggleFullscreen={toggleFullscreen}
                  showFullscreen
                />
              ) : (
                <div className="pdf-native-shell">
                  <div className="pdf-controls">
                    <button onClick={toggleFullscreen} className="control-btn" type="button">
                      <FaExpand /> {isFullscreen ? 'Sair do Full Screen' : 'Full Screen'}
                    </button>
                  </div>

                  <iframe
                    src={nativePdfViewerUrl}
                    title={conteudo.title}
                    className="pdf-viewer"
                    frameBorder="0"
                    sandbox="allow-scripts allow-same-origin"
                    allow="clipboard-read; clipboard-write"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="content-locked">
              {lockedPreview ? (
                <img
                  src={lockedPreview}
                  alt="Preview do PDF"
                  className="thumb-image"
                  style={{ filter: 'blur(2px) brightness(0.7)' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    opacity: 0.8
                  }}
                />
              )}
              <div className="file-preview">
                <FaFilePdf size={64} color="white" />
                <h3>Documento PDF</h3>
                <p>Visualize este documento completo</p>
                {conteudo.hasThumbnail && (
                  <small style={{ marginTop: '0.5rem', opacity: 0.8, fontSize: '0.85rem' }}>
                    📄 Preview da primeira página disponível
                  </small>
                )}
              </div>
              <button
                className="unlock-overlay"
                onClick={() => {
                  if (!isLoggedIn) {
                    setModalConfig({
                      title: 'Efetue Login ou Assinatura Necessária',
                      message:
                        'Você precisa de uma assinatura para ter acesso completo a este PDF. Se já é assinante, faça login!',
                      actionType: 'login_or_subscribe'
                    });
                  } else {
                    setModalConfig({
                      title: 'Assinatura Necessária',
                      message: 'Você precisa de uma assinatura para ter acesso completo a este PDF.',
                      actionType: 'content'
                    });
                  }
                  setShowModal(true);
                }}
              >
                <FaFilePdf size={24} />
                <span>Visualizar PDF</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    // DOC/PPT (Google Viewer)
    if (['DOC', 'DOCX', 'PPT', 'PPTX', 'DOCUMENTO', 'SLIDE'].includes(materialType)) {
      const lockedPreview = getLockedPreview(conteudo);
      const canUsePreviewImage =
        typeof lockedPreview === 'string' && /\.(png|jpe?g|webp|gif)$/i.test(lockedPreview);

      return (
        <div className="pdf-wrapper">
          {canPlay ? (
            <div className={`pdf-container ${isFullscreen ? 'fullscreen' : ''}`}>
              <div className="pdf-controls">
                <button onClick={toggleFullscreen} className="control-btn">
                  <FaExpand /> {isFullscreen ? 'Sair do Full Screen' : 'Full Screen'}
                </button>
              </div>
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                  materialUrl
                )}&embedded=true`}
                title={conteudo.title}
                className="pdf-viewer"
                frameBorder="0"
                sandbox="allow-scripts allow-same-origin"
                allow="clipboard-read; clipboard-write"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          ) : (
            <div className="content-locked">
              {canUsePreviewImage ? (
                <img
                  src={lockedPreview}
                  alt="Preview do documento"
                  className="thumb-image"
                  style={{ filter: 'blur(2px) brightness(0.7)' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    opacity: 0.8
                  }}
                />
              )}
              <div className="file-preview">
                <FaFilePdf size={64} color="white" />
                <h3>Documento {materialType}</h3>
                <p>Visualize este documento completo</p>
              </div>
              <button
                className="unlock-overlay"
                onClick={() => {
                  if (!isLoggedIn) {
                    setModalConfig({
                      title: 'Efetue Login ou Assinatura Necessária',
                      message:
                        'Você precisa de uma assinatura para ter acesso completo a este documento. Se já é assinante, faça login!',
                      actionType: 'login_or_subscribe'
                    });
                  } else {
                    setModalConfig({
                      title: 'Assinatura Necessária',
                      message: 'Você precisa de uma assinatura para ter acesso completo a este documento.',
                      actionType: 'content'
                    });
                  }
                  setShowModal(true);
                }}
              >
                <FaFilePdf size={24} />
                <span>Visualizar Documento</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    // Imagem
    if (materialType === 'PNG' || materialType === 'JPG' || materialType === 'JPEG') {
      const lockedPreview = getLockedPreview(conteudo);
      return (
        <div className="image-wrapper">
          {canPlay ? (
            <div className={`image-container ${isFullscreen ? 'fullscreen' : ''}`}>
              <div className="image-controls">
                <button onClick={toggleFullscreen} className="control-btn">
                  <FaExpand /> {isFullscreen ? 'Sair do Full Screen' : 'Full Screen'}
                </button>
              </div>
              <img
                src={materialUrl}
                alt={conteudo.title}
                className="content-image"
                onClick={toggleFullscreen}
              />
            </div>
          ) : (
            <div className="content-locked">
              {lockedPreview ? (
                <img
                  src={lockedPreview}
                  alt="Preview"
                  className="thumb-image"
                  style={{ filter: 'blur(8px) brightness(0.6)' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
              <div className="file-preview">
                <FaImage size={64} color="white" />
                <h3>Imagem</h3>
                <p>Visualize esta imagem em alta qualidade</p>
              </div>
              <button
                className="unlock-overlay"
                onClick={() => {
                  if (!isLoggedIn) {
                    setModalConfig({
                      title: 'Efetue Login ou Assinatura Necessária',
                      message:
                        'Você precisa de uma assinatura para visualizar esta imagem. Se já é assinante, faça login!',
                      actionType: 'login_or_subscribe'
                    });
                  } else {
                    setModalConfig({
                      title: 'Assinatura Necessária',
                      message: 'Você precisa de uma assinatura para visualizar esta imagem.',
                      actionType: 'content'
                    });
                  }
                  setShowModal(true);
                }}
              >
                <FaImage size={24} />
                <span>Visualizar Imagem</span>
              </button>
            </div>
          )}
        </div>
      );
    }

    // Fallback - quando é apenas TEXT com linkRelacionado
    if (materialType === 'TEXT' && conteudo.linkRelacionado) {
      // Se tem linkRelacionado de vídeo, mostrar como preview de vídeo
      if (isYouTubeLink(conteudo.linkRelacionado) || isVimeoLink(conteudo.linkRelacionado)) {
        const thumbnail = getVideoThumbnail(conteudo.linkRelacionado);
        return (
          <div className="video-wrapper">
            <div className="content-locked">
              {thumbnail && (
                <img
                  src={thumbnail}
                  alt="Preview do vídeo"
                  className="thumb-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              <div className="file-preview">
                <FaPlay size={64} color="white" />
                <h3>Vídeo Relacionado</h3>
                <p>Clique para acessar o vídeo</p>
              </div>

              <button
                className="play-overlay"
                onClick={() => {
                  window.open(conteudo.linkRelacionado, '_blank');
                }}
              >
                <FaPlay size={32} />
              </button>
            </div>
          </div>
        );
      }

      // Se tem outro tipo de linkRelacionado
      return (
        <div className="content-wrapper">
          <div className="link-preview">
            <h3>Link Relacionado</h3>
            <a
              href={conteudo.linkRelacionado}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 2rem',
                background: '#3498db',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600'
              }}
            >
              Acessar Link
            </a>
          </div>
        </div>
      );
    }

    // Fallback original
    return (
      <div className="content-wrapper">
        <div className="unsupported-content">
          <p>Tipo de conteúdo não suportado: {materialType}</p>
        </div>
      </div>
    );
  };

  if (!conteudo) return <p>Carregando conteúdo...</p>;

  const isEspec = isEspecialista(conteudo);
  const canDownloadPdf =
    !isStudentMaterial &&
    canPlay &&
    getMaterialType(conteudo) === 'PDF' &&
    conteudo.allowDownload === true &&
    !!getMaterialUrl(conteudo);

  return (
    <div className="conteudo-page">
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>{modalConfig.title}</h3>
            <p>{modalConfig.message}</p>
            <div className="modal-buttons">
              {modalConfig.actionType === 'content' ? (
                <>
                  <button onClick={() => { setShowModal(false); navigate('/premium'); }}>
                    Assinar
                  </button>
                  <button onClick={() => setShowModal(false)}>Fechar</button>
                </>
              ) : modalConfig.actionType === 'login_or_subscribe' ? (
                <>
                  <button onClick={() => { setShowModal(false); navigate('/login'); }}>
                    Login
                  </button>
                  <button onClick={() => { setShowModal(false); navigate('/premium'); }}>
                    Assinar
                  </button>
                  <button onClick={() => setShowModal(false)}>Fechar</button>
                </>
              ) : (
                <>
                  <button onClick={() => { setShowModal(false); navigate('/login'); }}>
                    Fazer Login
                  </button>
                  <button onClick={() => setShowModal(false)}>Fechar</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isFullscreen && (
        <div className="fullscreen-overlay" onClick={toggleFullscreen}>
          <button className="close-fullscreen" onClick={toggleFullscreen}>
            <FaTimes size={24} />
          </button>
        </div>
      )}

      <button className="botao-voltar" onClick={() => navigate(-1)}>
        ← Voltar
      </button>

      <div className="conteudo-grid">
        <div className="info-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{conteudo.title}</h2>

            {/* Botão Compartilhar */}
            <button
              onClick={handleShare}
              title="Compartilhar"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px',
                borderRadius: '8px',
                color: '#374151'
              }}
            >
              <FaShareAlt />
            </button>

            {shareMsg && (
              <small style={{ color: '#10b981', fontWeight: 600 }}>{shareMsg}</small>
            )}

            {/* Badge de Material do Aluno */}
            {isStudentMaterial && !isEspec && (
              <span
                style={{
                  fontSize: '0.75rem',
                  background: 'rgba(245, 158, 11, 0.1)',
                  color: '#f59e0b',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  marginLeft: '0.5rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <FaUserGraduate /> MATERIAL DO ALUNO
              </span>
            )}

            {/* Badge de Especialista/Conteúdo Verificado */}
            {isEspec && (
              <span
                style={{
                  fontSize: '0.75rem',
                  background: 'rgba(34, 197, 94, 0.1)',
                  color: '#22c55e',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  marginLeft: '0.5rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <FaCheckCircle /> CONTEÚDO VERIFICADO
              </span>
            )}
          </div>

          <p>
            <FaUser className="icon" />
            {conteudo.authorName || 'Conecta Admin Master'}
            {isEspec && (
              <span style={{
                marginLeft: '0.5rem',
                fontSize: '0.8rem',
                color: '#22c55e',
                fontWeight: '600'
              }}>
                (Especialista)
              </span>
            )}
            {isStudentMaterial && !isEspec && (
              <span style={{
                marginLeft: '0.5rem',
                fontSize: '0.8rem',
                color: '#f59e0b',
                fontWeight: '600'
              }}>
                (Aluno)
              </span>
            )}
          </p>
          <p>
            <FaCalendarAlt className="icon" /> {formatCreatedAt(conteudo.createdAt)}
          </p>
          <p>
            <FaTags className="icon" /> {conteudo.subcategory}
          </p>
          <p className="descricao" dangerouslySetInnerHTML={{ __html: formatText(conteudo.description) }}></p>

          {/* Conteúdo/Pergunta para materiais de alunos */}
          {isStudentMaterial && conteudo.conteudo && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4
                style={{
                  color: '#2c3e50',
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaUser style={{ fontSize: '0.9rem' }} />
                Pergunta do Aluno:
              </h4>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '1rem',
                  borderLeft: '4px solid #f59e0b',
                  lineHeight: '1.6',
                  color: '#374151'
                }}
                dangerouslySetInnerHTML={{ __html: formatText(conteudo.conteudo) }}
              ></div>
            </div>
          )}

          {/* Resposta do Professor */}
          {isStudentMaterial && conteudo.resposta && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4
                style={{
                  color: '#2c3e50',
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaReply style={{ fontSize: '0.9rem' }} />
                Resposta do Professor:
              </h4>
              <div
                style={{
                  background: '#f0f9ff',
                  border: '1px solid #e0f2fe',
                  borderRadius: '8px',
                  padding: '1rem',
                  borderLeft: '4px solid #3498db',
                  lineHeight: '1.6',
                  color: '#374151'
                }}
                dangerouslySetInnerHTML={{ __html: formatText(conteudo.resposta) }}
              ></div>

              {conteudo.videoResposta && (
                <div style={{ marginTop: '1rem' }}>
                  <h5 style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Resposta em Vídeo:
                  </h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <img
                      src={getVideoThumbnail(conteudo.videoResposta)}
                      alt="Resposta em vídeo"
                      style={{
                        width: '120px',
                        height: '68px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        border: '1px solid #e2e8f0'
                      }}
                      onClick={() => window.open(conteudo.videoResposta, '_blank')}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <a
                      href={conteudo.videoResposta}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#3498db',
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                    >
                      <FaPlay /> Assistir Resposta
                    </a>
                  </div>
                </div>
              )}

              {conteudo.respondidoPor && (
                <div
                  style={{
                    marginTop: '1rem',
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}
                >
                  Respondido por {conteudo.respondidoPor} em{' '}
                  {conteudo.respondidoEm?.toDate
                    ? conteudo.respondidoEm.toDate().toLocaleDateString('pt-BR')
                    : new Date(conteudo.respondidoEm).toLocaleDateString('pt-BR')}
                </div>
              )}
            </div>
          )}

          <div className="content-type-info">
            <span className={`type-badge ${getMaterialType(conteudo)?.toLowerCase()}`}>
              {(getMaterialType(conteudo) === 'PDF' ||
                getMaterialType(conteudo) === 'DOC' ||
                getMaterialType(conteudo) === 'DOCX' ||
                getMaterialType(conteudo) === 'PPT' ||
                getMaterialType(conteudo) === 'PPTX' ||
                getMaterialType(conteudo) === 'DOCUMENTO' ||
                getMaterialType(conteudo) === 'SLIDE') && <FaFilePdf />}
              {(getMaterialType(conteudo) === 'PNG' || getMaterialType(conteudo) === 'JPG') && <FaImage />}
              {(getMaterialType(conteudo) === 'YOUTUBE' || getMaterialType(conteudo) === 'VIMEO') && <FaPlay />}
              {getMaterialType(conteudo)?.toUpperCase()}
            </span>

            {/* indicador de preview para PDFs */}
            {getMaterialType(conteudo)?.toUpperCase() === 'PDF' && conteudo.hasThumbnail && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginLeft: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(40, 167, 69, 0.1)',
                  color: '#28a745',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                ✓ Preview Gerado
              </span>
            )}

            {['DOC', 'DOCX', 'PPT', 'PPTX', 'DOCUMENTO', 'SLIDE'].includes(getMaterialType(conteudo)?.toUpperCase()) && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginLeft: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(40, 167, 69, 0.1)',
                  color: '#28a745',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                ✓ Preview Gerado
              </span>
            )}

            {/* indicador de thumbnail para vídeos */}
            {(getMaterialType(conteudo)?.toUpperCase() === 'YOUTUBE' || getMaterialType(conteudo)?.toUpperCase() === 'VIMEO') && getVideoThumbnail(getMaterialUrl(conteudo)) && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginLeft: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(52, 152, 219, 0.1)',
                  color: '#3498db',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                🎬 Thumbnail Disponível
              </span>
            )}

            {/* Indicador de Link Relacionado para materiais de aluno */}
            {isStudentMaterial && conteudo.linkRelacionado && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  marginLeft: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(156, 39, 176, 0.1)',
                  color: '#9c27b0',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                🔗 Link Relacionado
              </span>
            )}
          </div>

          <div className="acoes-video">
            {canDownloadPdf && (
              <button
                onClick={handleDownloadPdf}
                className="download-btn"
                disabled={isDownloading}
                type="button"
              >
                <FaDownload /> {isDownloading ? 'Baixando...' : 'Baixar PDF'}
              </button>
            )}
            <button onClick={curtirVideo} className="like-btn">
              <FaHeart /> Curtir ({curtidas})
            </button>
            <button
              onClick={favoritarVideo}
              className={`fav-btn ${favoritado ? 'ativo' : ''}`}
            >
              <FaBookmark /> {favoritado ? 'Favoritado' : 'Favoritar'}
            </button>
          </div>
        </div>

        {renderContent()}
      </div>

      <div className="comentarios-section">
        <h3>Comentários ({comentarios.length})</h3>

        <form onSubmit={enviarComentario} className="comentario-form">
          <textarea
            placeholder="Escreva um comentário..."
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
          <button type="submit">Enviar</button>
        </form>

        <div className="lista-comentarios">
          {comentarios.map((c) => (
            <div key={c.id} className="comentario-card">
              <p className="comentario-meta">
                <strong>{c.autor}</strong> –{' '}
                {c.createdAt?.seconds
                  ? new Date(c.createdAt.seconds * 1000).toLocaleDateString('pt-BR')
                  : '...'}
              </p>
              <p className="comentario-texto" dangerouslySetInnerHTML={{ __html: formatText(c.texto) }}></p>
              <button onClick={() => curtirComentario(c.id)} className="like-btn">
                <FaHeart /> {c.likes || 0}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VerConteudo;
